import { contactRecord } from "../schemas/contacts";
import { email as emailSchema } from "../schemas/common";
import { normalizePhone } from "../phone";
import { evaluation, zodIssues, type ValidationAction, type ValidationIssue } from "./issues";

type ContactRecord = Record<string, unknown>;
const text = (value: unknown) => typeof value === "string" ? value.trim() : "";
const emailKey = (value: unknown) => text(value).toLowerCase();
const phoneKey = (value: unknown) => normalizePhone(typeof value === "string" ? value : null);

/** Prospective identities, before any find-or-create enrichment. Existing IDs
 * are authoritative; normalized delivery routes identify an unsaved candidate. */
export function sameContactIdentity(left: ContactRecord | null | undefined, right: ContactRecord | null | undefined): boolean {
  if (!left || !right) return false;
  if (typeof left.id === "string" && typeof right.id === "string") return left.id === right.id;
  const email = emailKey(left.email), phone = phoneKey(left.phone);
  return Boolean((email && email === emailKey(right.email)) || (phone && phone === phoneKey(right.phone)));
}

export interface ContactFacts {
  action?: ValidationAction;
  /** Lenient inquiry inputs retain malformed delivery evidence for staff review. */
  policy?: "manual" | "intake";
  before?: ContactRecord;
  changedFields?: readonly string[];
  /** Already authorized, exact-match candidates only; never pass hidden records. */
  duplicates?: readonly ContactRecord[];
  checksComplete?: boolean;
  requireCompany?: boolean;
}

export function validateContact(record: ContactRecord, facts: ContactFacts = {}) {
  const entity = { type: "contact" as const, ...(typeof record.id === "string" ? { id: record.id } : {}) };
  const issues: ValidationIssue[] = [];
  const parsed = contactRecord.safeParse(record);
  if (!parsed.success) for (const issue of zodIssues(parsed.error.issues, entity, "contact.save")) {
    const field = issue.fields[0];
    const unchanged = facts.before && facts.changedFields && !facts.changedFields.includes(field) && Object.is(facts.before[field], record[field]);
    issues.push({ ...issue, code: `contact.${field || "record"}_invalid`,
      kind: facts.policy === "intake" && field === "email" ? "warning" : "error",
      blocks: [...(!unchanged && !(facts.policy === "intake" && field === "email") ? ["contact.save" as const] : []), ...(field === "email" ? ["comms.send_email" as const] : [])],
    });
  }
  const validEmail = emailSchema.safeParse(record.email).success;
  const phone = phoneKey(record.phone);
  if (text(record.phone) && !phone) issues.push({ code: "contact.phone_unusable", kind: "warning", entity, fields: ["phone"], message: "Keep this number for reference, but enter a usable phone number before texting.", blocks: ["comms.send_sms"] });
  if (!validEmail && !phone) issues.push({ code: "contact.delivery_missing", kind: "missing", entity, fields: ["email", "phone"], message: "Add an email address or usable phone number for contact delivery.", blocks: ["comms.send_email", "comms.send_sms"] });
  else if (facts.action === "comms.send_email" && !validEmail) issues.push({ code: "contact.email_missing", kind: "missing", entity, fields: ["email"], message: "Add a valid email address before sending email.", blocks: ["comms.send_email"] });
  else if (facts.action === "comms.send_sms" && !phone) issues.push({ code: "contact.phone_missing", kind: "missing", entity, fields: ["phone"], message: "Add a usable phone number before texting.", blocks: ["comms.send_sms"] });
  if (facts.requireCompany && !text(record.companyName)) issues.push({ code: "contact.company_missing", kind: "missing", entity, fields: ["companyName"], message: "Add the company name for this action.", blocks: ["contract.send"] });
  for (const candidate of facts.duplicates ?? []) {
    if (candidate.id === record.id || typeof candidate.id !== "string") continue;
    if (!((validEmail && emailKey(record.email) === emailKey(candidate.email)) || (phone && phone === phoneKey(candidate.phone)))) continue;
    const other = { type: "contact" as const, id: candidate.id };
    issues.push({ code: "contact.duplicate_candidate", kind: "warning", entity, fields: ["email", "phone"], message: "Another contact has the same email or phone. Review before creating a duplicate.", blocks: [], related: [other], ...(entity.id ? { pair: [entity, other] as [typeof entity, typeof other] } : {}) });
  }
  return evaluation(issues, facts.checksComplete ?? false);
}
